import React from "react";
import { Navigate, useLocation, useOutlet } from "react-router";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import {
  AppHeader,
  PrimaryCard,
  FlyoutContainer,
  RouteContainerStateProvider,
  utils,
  Dialogs,
  SidebarTypes,
  NavigationExpandModes,
  AppSideBar,
} from "@ucl/library";
import logo from "@assets/bank-logo.svg";
import { routeObject } from "@config/routeConfig";
import PagesLayoutFooter from "./PagesLayoutFooter";
import { useUser } from "../contexts/UserContext/UserContext";
import { useAuth0 } from "@auth0/auth0-react";
import { DropDownButtonComponent } from "@syncfusion/ej2-react-splitbuttons";
import { getAuth0Config } from "../config/variables";
import classNames from "classnames";
import "./PagesLayout.scss";

export interface PagesLayoutComponentProps {
  redirectPath: string;
  notAvailablePath?: string;
  hideContactButton?: boolean;
}

const PagesLayoutComponent: React.FC<PagesLayoutComponentProps> = (props) => {
  const outlet = useOutlet();
  const location = useLocation();
  const { user, logout, clientType } = useUser();
  const { logout: auth0Logout } = useAuth0();

  const ErrorFallback = (args: FallbackProps) => {
    return (
      <PrimaryCard headerContent="Something went wrong">
        <div role="alert">
          <pre>{args.error.message}</pre>
          <button onClick={args.resetErrorBoundary}>Try again</button>
        </div>
      </PrimaryCard>
    );
  };

  // Guard
  const currentRoute = utils.routes.findRoute(routeObject, location.pathname);

  if (currentRoute && currentRoute.isAvailable === false) {
    let guardRedirectPath =
      location.pathname !== props.notAvailablePath ? props.notAvailablePath : null;

    if (!guardRedirectPath && location.pathname !== props.redirectPath) {
      guardRedirectPath = props.redirectPath;
    }

    // Guard Redirect
    return <Navigate to={guardRedirectPath || props.redirectPath} replace />;
  }

  return (
    <div className="page">
      <AppHeader appLogo={<img src={logo} alt="Best Bank" />} appTitle="Best Bank">
        {user && (
          <div className="app-header-user-settings">
            <DropDownButtonComponent
              content={user.firstName ? `${user?.firstName} ${user?.lastName}`.trim() : "Sign Out"}
              items={[
                {
                  text: "Sign Out",
                },
              ]}
              select={(args) => {
                if (args.item.text === "Sign Out") {
                  if (clientType) {
                    const auth0Config = getAuth0Config(clientType);
                    auth0Logout({
                      logoutParams: {
                        returnTo: auth0Config.logoutUri,
                      },
                    }).then(() => {
                      logout();
                    });
                  }
                }
              }}
            />
          </div>
        )}
      </AppHeader>
      <div className={classNames("content", {
            "content-with-sidebar": clientType === "banker",
          })}>
        {clientType === "banker" && routeObject.length > 0 && user && (
          <AppSideBar
            uclRouteObject={routeObject}
            isOpen={false}
            navigationExpandMode={NavigationExpandModes.AllClosed}
            dockSidebarType={SidebarTypes.Column}
            showSearchBar={false}
            useNodeClicked={true}
            sideBarSettings={{
              width: 290,
              dockSize: 100,
              enableDock: true,
            }}
          />
        )}
        <div
          className={classNames("page-main-content", {
            "page-main-content-with-sidebar": clientType === "banker",
          })}
        >
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <RouteContainerStateProvider routes={routeObject}>
              {outlet || <Navigate to={props.redirectPath} replace />}
            </RouteContainerStateProvider>
            <FlyoutContainer />
          </ErrorBoundary>
          <div className="page-main-content-footer"></div>
        </div>
        <Dialogs />
        <PagesLayoutFooter hideContactButton={props.hideContactButton} />
      </div>
    
    </div>
  );
};

export const PagesLayout = PagesLayoutComponent;
export default PagesLayout;
